'use strict';


import { createLabel, createNameLabel, latLngAltToVector3, Layer } from './Layer';
import {
    Box2,
    BufferGeometry, LinearSRGBColorSpace,
    Vector2,
    Vector3,
    Color
} from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { EnvironmentLayer } from '../Environment/EnvironmentLayers/EnvironmentLayer';

export class Railway extends Layer {

    constructor (parent, data) {
        super(parent, data);

        this.createLayer();
        this.animateCamera = true;
    }

    show() {
        super.show();
        EnvironmentLayer.animateCameraShow();
    }

    hide() {
        super.hide();
        EnvironmentLayer.animateCameraHide();
    }

    async createLayer() {

        this.data = await this.response;

        if (!this.data) {
            this.btn?.remove();
            return ;
        }

        const center = {lat: Number(config.localization.latitude), lng: Number(config.localization.longitude)};
        const zeroVector = new Vector3();
        const box = new Box2();
        const minV2 = new Vector2();
        const maxV2 = new Vector2();
        const platformCenter = new Vector2();

        const platforms = this.data.filter(el => el.type === 'way');
        platforms.forEach((platform,index) => {
            minV2.set(platform.bounds.minlat,platform.bounds.minlon);
            maxV2.set(platform.bounds.maxlat,platform.bounds.maxlon);
            box.set(minV2,maxV2).getCenter(platformCenter);
            const point = {lat: platformCenter.x, lng: platformCenter.y};
            const vector = latLngAltToVector3(point,center);
            const distance = Math.floor(zeroVector.distanceTo(vector));

            if (distance < this.layer.distance) {
                const color = platform.tags.building ? '#0814ff' : '#f50202';
                const names = {};
                config.languages.forEach(lang => {
                    if (platform.tags.building) {
                        names[lang] = config.dictionary['Station'] ? config.dictionary['Station'][lang] : 'Station';
                    } else {
                        names[lang] = config.dictionary['Platform'] ? config.dictionary['Platform'][lang] : 'Platform';
                    }
                });

                const label = createLabel(names,'icon-subway2',color,distance);
                label.position.copy(vector);

                const poiData = {
                    id: index,
                    to: point,
                    from: center,
                    route: null
                };

                label.element.onclick = () => this.poiHandler(poiData);
    
                this.labels.push({
                    label,
                    distance
                });
                this.routeGroup.add(label);
            }
        });

        this.data = this.data.filter(el => el.type === 'relation');

        this.data.forEach(relation => {
            const routeType = relation.tags.route;
            const proposedState = relation.tags.state === 'proposed';
            const constructionState = relation.tags.state === 'construction';
            const relationNames = {};

            config.languages.forEach(lang => {
               if (relation.tags.hasOwnProperty(`name:${lang}`)) {
                   relationNames[lang] = relation.tags[`name:${lang}`];
               } else {
                   relationNames[lang] = relation.tags.name;
                   relationNames[lang] = config.dictionary[relation.tags.name] ? config.dictionary[relation.tags.name][lang] : relation.tags.name;
               }
            });

            const ways = relation.members.filter(el => el.type === 'way');
            let reverse = 0;
            const relationPoints = [];

            ways.forEach((way,index) => {
                const points = [];

                way.geometry.forEach(coords => {
                    const point = {lat: coords.lat, lng: coords.lon};
                    const vector = latLngAltToVector3(point,center);
                    const distance = vector.distanceTo(zeroVector);

                    if (distance < this.layer.distance) {

                        if (coords.elevation) {
                            vector.y = coords.elevation;
                        }

                        points.push(vector);
                    }
                });

                if (!points.length) return;
                relationPoints.push(...points);

                let direction = this.layer.direction === 'counterclockwise' ? -1 : 1;

                if (reverse % 2 !== 0) {
                    direction *= -1;
                }

                reverse++;

                const geometry = new BufferGeometry().setFromPoints(points);
                const lineGeometry = new LineGeometry().setPositions(geometry.getAttribute('position').array);
                const lineMaterial = new LineMaterial({
                    color: new Color().setStyle(proposedState ? this.layer.colors['proposed'] : constructionState ? this.layer.colors['construction'] : this.layer.colors[routeType],LinearSRGBColorSpace),
                    linewidth: 5,
                    dashed: false,
                    opacity: 0.9,
                    alphaToCoverage: true
                });

                lineMaterial.resolution.set( window.innerWidth / 4, window.innerHeight / 4 );

                const  line = new Line2(lineGeometry,lineMaterial);
                line.computeLineDistances();
                line.scale.set(1,1,1);


                const dashMaterial = new LineMaterial({
                    color: new Color().setStyle('#ffffff',LinearSRGBColorSpace),
                    linewidth: 5,
                    dashed: true,
                    dashOffset: 1,
                    opacity: 0.6,
                    gapSize: 20,
                    dashSize: 10,
                    alphaToCoverage: true
                });

                dashMaterial.resolution.set( window.innerWidth / 4, window.innerHeight / 4 );

                const dashLine = new Line2(lineGeometry,dashMaterial);
                dashLine.computeLineDistances();
                dashLine.scale.set(1,1,1);

                dashMaterial.tick = () => {
                    dashMaterial.dashOffset += direction;
                };

                this.animations.push(dashMaterial);
                this.routeGroup.add(line);
                this.routeGroup.add(dashLine);
            });

            //Relation name

            if (relationPoints.length) {
                if (relation.tags.hasOwnProperty('from')) {
                    const relationStart = relationPoints[0];
                    const relationName = createNameLabel(relationNames);
                    relationName.position.copy(relationStart);
                    this.routeGroup.add(relationName);

                } else {
                    const max = Math.max(...relationPoints.map(point => point.distanceTo(zeroVector)));
                    const relationStart = relationPoints[0];
                    const relationEnd = relationPoints.find(el => el.length() === max);

                    [relationStart,relationEnd].forEach(point => {
                        const relationName = createNameLabel(relationNames);
                        relationName.position.copy(point);
                        this.routeGroup.add(relationName);
                    });
                }
            }

        });

    }

}
